import React from "react";
import Modal from "../Modal/Modal";
import { showToast } from "../../../utils/toast";
import "./ShareModal.css";

const ShareModal = ({
  isOpen,
  onClose,
  title = "Share this card",
  url = window.location.href,
  image = null,
  description = "Checkout this card on Sleevr"
}) => {
  const handleWhatsAppShare = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(description + " " + url)}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleXShare = () => {
    const xUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(description)}&url=${encodeURIComponent(url)}`;
    window.open(xUrl, "_blank");
  };

  const handleEmailShare = () => {
    const emailUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(description + "\n\n" + url)}`;
    window.open(emailUrl, "_blank");
  };

  const handleFacebookShare = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(facebookUrl, "_blank");
  };

  /**
   * Copy, and only claim success if it actually copied.
   *
   * This used to fire the success toast unconditionally, on the line after an
   * un-awaited `navigator.clipboard.writeText(url)`. `writeText` REJECTS on any
   * insecure origin, when the document is not focused, and when the permission
   * is denied — none of which throws synchronously — so the failure was an
   * unhandled rejection and the user got a green "Link copied!" for a clipboard
   * that still held whatever was in it before. Nothing happened, and the app
   * said it had.
   *
   * The fallback is a real one: a hidden textarea plus `execCommand('copy')`,
   * which still works where the async API is blocked. Only if BOTH fail do we
   * say so.
   */
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      showToast.success("Link copied to clipboard!");
      return true;
    } catch {
      try {
        const field = document.createElement("textarea");
        field.value = url;
        field.setAttribute("readonly", "");
        field.style.position = "fixed";
        field.style.opacity = "0";
        document.body.appendChild(field);
        field.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(field);
        if (!ok) throw new Error("execCommand copy returned false");
        showToast.success("Link copied to clipboard!");
        return true;
      } catch {
        showToast.error("We could not copy the link. Long-press the address bar to copy it.");
        return false;
      }
    }
  };

  const handleQuickShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: description, url });
        return;
      } catch (error) {
        // A user dismissing the OS share sheet is not an error and must not
        // fall through to a copy they did not ask for.
        if (error && error.name === "AbortError") return;
      }
    }
    await handleCopyLink();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
    >
      <div className="share-modal-content">
        {image && (
          <div className="share-image-preview">
            <img src={image} alt="Share preview" />
          </div>
        )}
        
        <div className="share-options">
          <button className="share-option whatsapp" onClick={handleWhatsAppShare}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.464 3.488A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" fill="#25D366"/>
            </svg>
            <span>WhatsApp</span>
          </button>
          
          <button className="share-option x" onClick={handleXShare}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="#000000"/>
            </svg>
            <span>X</span>
          </button>
          
          <button className="share-option facebook" onClick={handleFacebookShare}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M22 12C22 6.48 17.52 2 12 2C6.48 2 2 6.48 2 12C2 16.84 5.44 20.87 10 21.8V15H8V12H10V9.5C10 7.57 11.57 6 13.5 6H16V9H14C13.45 9 13 9.45 13 10V12H16V15H13V21.95C18.05 21.45 22 17.19 22 12Z" fill="#1877F2"/>
            </svg>
            <span>Facebook</span>
          </button>
          
          <button className="share-option email" onClick={handleEmailShare}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="#EA4335"/>
            </svg>
            <span>Email</span>
          </button>
        </div>
        
        <div className="share-actions">
          <button className="copy-link-btn" onClick={handleCopyLink}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 8.33333H13.3333V5C13.3333 3.15833 11.8417 1.66667 10 1.66667C8.15833 1.66667 6.66667 3.15833 6.66667 5V8.33333H5C4.08333 8.33333 3.33333 9.08333 3.33333 10V16.6667C3.33333 17.5833 4.08333 18.3333 5 18.3333H15C15.9167 18.3333 16.6667 17.5833 16.6667 16.6667V10C16.6667 9.08333 15.9167 8.33333 15 8.33333ZM8.33333 5C8.33333 4.08333 9.08333 3.33333 10 3.33333C10.9167 3.33333 11.6667 4.08333 11.6667 5V8.33333H8.33333V5ZM15 16.6667H5V10H15V16.6667ZM10 14.1667C10.9167 14.1667 11.6667 13.4167 11.6667 12.5C11.6667 11.5833 10.9167 10.8333 10 10.8333C9.08333 10.8333 8.33333 11.5833 8.33333 12.5C8.33333 13.4167 9.08333 14.1667 10 14.1667Z" fill="#9BA3AF"/>
            </svg>
            <span>Copy link</span>
          </button>
          
          <button className="quick-share-btn" onClick={handleQuickShare}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15.8333 13.3333C15.0583 13.3333 14.375 13.6417 13.8917 14.1417L7.7 10.775C7.74167 10.5167 7.75 10.2583 7.7 10C7.75 9.74167 7.74167 9.48333 7.7 9.225L13.8 5.9C14.3 6.43333 14.9917 6.75 15.8333 6.75C17.2167 6.75 18.3333 5.63333 18.3333 4.25C18.3333 2.86667 17.2167 1.75 15.8333 1.75C14.45 1.75 13.3333 2.86667 13.3333 4.25C13.3333 4.50833 13.375 4.75833 13.4333 5L7.33333 8.325C6.83333 7.79167 6.15 7.5 5.33333 7.5C3.95 7.5 2.83333 8.61667 2.83333 10C2.83333 11.3833 3.95 12.5 5.33333 12.5C6.15 12.5 6.83333 12.2083 7.33333 11.675L13.5167 15.05C13.4583 15.275 13.425 15.5083 13.425 15.75C13.425 17.0917 14.5 18.1667 15.8417 18.1667C17.1833 18.1667 18.2583 17.0917 18.2583 15.75C18.2583 14.4083 17.175 13.3333 15.8333 13.3333ZM15.8333 3.41667C16.2917 3.41667 16.6667 3.79167 16.6667 4.25C16.6667 4.70833 16.2917 5.08333 15.8333 5.08333C15.375 5.08333 15 4.70833 15 4.25C15 3.79167 15.375 3.41667 15.8333 3.41667ZM5.33333 10.8333C4.875 10.8333 4.5 10.4583 4.5 10C4.5 9.54167 4.875 9.16667 5.33333 9.16667C5.79167 9.16667 6.16667 9.54167 6.16667 10C6.16667 10.4583 5.79167 10.8333 5.33333 10.8333ZM15.8333 16.5833C15.375 16.5833 15 16.2083 15 15.75C15 15.2917 15.375 14.9167 15.8333 14.9167C16.2917 14.9167 16.6667 15.2917 16.6667 15.75C16.6667 16.2083 16.2917 16.5833 15.8333 16.5833Z" fill="#9BA3AF"/>
            </svg>
            <span>Quick Share</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ShareModal;